import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiBaseUrl } from '../config.js';

const PaymentResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const collection_id = urlParams.get('collection_id');
    const collection_status = urlParams.get('collection_status');
    const payment_id = urlParams.get('payment_id');
    const status = urlParams.get('status');
    const external_reference = urlParams.get('external_reference');
    const payment_type = urlParams.get('payment_type');
    const merchant_order_id = urlParams.get('merchant_order_id');
    const preference_id = urlParams.get('preference_id');
    const site_id = urlParams.get('site_id');
    const processing_mode = urlParams.get('processing_mode');
    const merchant_account_id = urlParams.get('merchant_account_id');
    const mock = urlParams.get('mock');

    setPaymentData({
      collection_id,
      collection_status,
      payment_id,
      status,
      external_reference,
      payment_type,
      merchant_order_id,
      preference_id,
      site_id,
      processing_mode,
      merchant_account_id,
      mock
    });

    // Determine payment status based on URL parameters
    if (status === 'approved' || collection_status === 'approved' || mock === 'true') {
      setPaymentStatus('success');
      
      // Prepare payment success data
      const paymentSuccess = {
        status: 'approved',
        payment_id: payment_id || `mock_${Date.now()}`,
        external_reference,
        timestamp: Date.now(),
        mock: mock === 'true'
      };

      // Multi-method communication to parent tab
      try {
        // Method 1: BroadcastChannel (most reliable)
        if (typeof BroadcastChannel !== 'undefined') {
          const channel = new BroadcastChannel('mercadopago_payment');
          channel.postMessage({
            type: 'PAYMENT_SUCCESS',
            data: paymentSuccess
          });
          
          // Close channel after sending
          setTimeout(() => channel.close(), 1000);
        }
      } catch (error) {
      }

      try {
        // Method 2: LocalStorage (fallback)
        localStorage.setItem('mercadopago_payment_success', JSON.stringify(paymentSuccess));
        
        // Trigger storage event manually
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'mercadopago_payment_success',
          newValue: JSON.stringify(paymentSuccess),
          url: window.location.href
        }));
      } catch (error) {
      }

      try {
        // Method 3: PostMessage to opener (if opened from window.open)
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            type: 'MERCADOPAGO_PAYMENT_SUCCESS',
            data: paymentSuccess
          }, window.location.origin);
        }
      } catch (error) {
      }
    } else if (status === 'pending' || collection_status === 'pending') {
      setPaymentStatus('pending');
    } else if (status === 'rejected' || collection_status === 'rejected') {
      setPaymentStatus('failure');
    } else {
      setPaymentStatus('unknown');
    }

    // Check payment status with backend if external_reference exists
    if (external_reference) {
      checkPaymentStatus(external_reference);
    }
  }, [location]);

  const checkPaymentStatus = async (external_reference) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/payment/status/${external_reference}`);
    } catch (error) {
    }
  };

  const getStatusIcon = () => {
    switch (paymentStatus) {
      case 'success': return '✅';
      case 'pending': return '⏳';
      case 'failure': return '❌';
      case 'loading': return '🔄';
      default: return '❓';
    }
  };

  const getStatusTitle = () => {
    switch (paymentStatus) {
      case 'success': return '¡Pago Exitoso!';
      case 'pending': return 'Pago Pendiente';
      case 'failure': return 'Pago Rechazado';
      case 'loading': return 'Verificando Pago...';
      default: return 'Estado Desconocido';
    }
  };

  const getStatusMessage = () => {
    switch (paymentStatus) {
      case 'success': 
        if (paymentData?.mock) {
          return 'Pago simulado exitoso para pruebas. Te enviaremos un email de confirmación.';
        }
        return 'Tu pago ha sido procesado exitosamente. Te enviaremos un email de confirmación.';
      case 'pending': 
        return 'Tu pago está siendo procesado. Te notificaremos cuando se complete.';
      case 'failure': 
        return 'Tu pago no pudo ser procesado. Por favor, intenta nuevamente.';
      case 'loading': 
        return 'Estamos verificando el estado de tu pago...';
      default: 
        return 'No pudimos determinar el estado de tu pago.';
    }
  };

  const getStatusColor = () => {
    switch (paymentStatus) {
      case 'success': return '#28a745';
      case 'pending': return '#ffc107';
      case 'failure': return '#dc3545';
      case 'loading': return '#007bff';
      default: return '#6c757d';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        maxWidth: '600px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        {/* Status Icon */}
        <div style={{
          fontSize: '64px',
          marginBottom: '20px'
        }}>
          {getStatusIcon()}
        </div>

        {/* Status Title */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: getStatusColor(),
          marginBottom: '16px'
        }}>
          {getStatusTitle()}
        </h1>

        {/* Status Message */}
        <p style={{
          fontSize: '16px',
          color: '#6c757d',
          lineHeight: '1.5',
          marginBottom: '30px'
        }}>
          {getStatusMessage()}
        </p>

        {/* Payment Details */}
        {paymentData && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '30px',
            textAlign: 'left'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#2c3e50',
              marginBottom: '12px',
              textAlign: 'center'
            }}>
              Detalles del Pago
            </h3>
            
            {paymentData.payment_id && (
              <div style={{ marginBottom: '8px' }}>
                <strong>ID de Pago:</strong> {paymentData.payment_id}
              </div>
            )}
            
            {paymentData.external_reference && (
              <div style={{ marginBottom: '8px' }}>
                <strong>Referencia:</strong> {paymentData.external_reference}
              </div>
            )}
            
            {paymentData.payment_type && (
              <div style={{ marginBottom: '8px' }}>
                <strong>Método de Pago:</strong> {paymentData.payment_type}
              </div>
            )}
            
            {paymentData.status && (
              <div style={{ marginBottom: '8px' }}>
                <strong>Estado:</strong> <span style={{ color: getStatusColor() }}>
                  {paymentData.status}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
          >
            🏠 Volver al Inicio
          </button>

          {paymentStatus === 'failure' && (
            <button
              onClick={() => window.history.back()}
              style={{
                padding: '12px 24px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1e7e34'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
            >
              🔄 Intentar Nuevamente
            </button>
          )}
        </div>

        {/* Support Info */}
        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: '#e9ecef',
          borderRadius: '8px'
        }}>
          <p style={{
            fontSize: '14px',
            color: '#6c757d',
            margin: '0',
            lineHeight: '1.4'
          }}>
            Si tienes problemas con tu pago, contáctanos en:<br/>
            <strong>info@tasacionesya.com</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
